<template>
  <div class="container">
    <!-- 日 / 週 / 月 選択エリア
      期間選択エリアの表示切替のトリガーです。 -->
    <div>
      <md-radio
        v-for="term in termTypes"
        :key="term.id"
        v-model="_termType"
        :value="term.id"
        >{{ term.label }}</md-radio
      >
    </div>
    <!-- 期間選択エリア -->
    <div>
      <transition mode="out-in">
        <!-- 週の選択 -->
        <div class="term-input" v-if="isWeekMode" key="w">
          <gl-input-date
            v-if="weekCountFrom"
            :min="minDate"
            :max="maxDate"
            :value.sync="_WeekFrom"
          />
          <gl-input-date
            v-if="weekCountTo"
            :min="minDate"
            :max="maxDate"
            :value.sync="_WeekTo"
          />
          <md-field class="divider">
            <md-select v-model="_weekType">
              <md-option
                v-for="week in weekTypes"
                :value="week.id"
                :key="week.id"
              >
                {{ week.label }}
              </md-option>
            </md-select>
          </md-field>
          <md-field class="week-count">
            <md-input
              v-model.number="weekCount"
              type="number"
              :min="1"
              :max="maxWeek"
              required
            ></md-input>
          </md-field>
          <div class="divider">
            <span>週間</span>
          </div>
        </div>
        <!-- 日の選択 -->
        <div class="term-input" v-if="isDateMode" key="d">
          <gl-input-date
            :min="minDate"
            :max="maxDate"
            :value.sync="_dateFrom"
          />
          <div class="divider">～</div>
          <gl-input-date :min="minDate" :max="maxDate" :value.sync="_dateTo" />
        </div>
        <!-- 月の選択 -->
        <div class="term-input" v-if="isMonthMode" key="m">
          <gl-select-month
            :min="minDate"
            :max="maxDate"
            :value.sync="monthFrom"
          />
          <div class="divider">～</div>
          <gl-select-month
            :min="minDate"
            :max="maxDate"
            :value.sync="monthTo"
          />
        </div>
      </transition>
    </div>
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </div>
</template>
<script>
import moment from 'moment'
import GlSelectMonth from '@/components/common/GlSelectMonth.vue'
import GlInputDate from '@/components/common/GlInputDate.vue'
import {
  getEndDateByWeekCount,
  getStartDateByWeekCount,
  dateLimit,
  weekMove,
  toBasicDateFormat,
} from '@/util/date'
import ErrorReport from '@/mixins/ErrorReport'
import QueryFilterRestoreMixin from '@/mixins/QueryFilterRestoreMixin'

/**
 * 期間選択コンポーネント
 * 内部的には開始、終了日だけを保持し、週や月の値は現在選択されている日付
 * から算出して取得・設定します。
 * なので、選択方式に週や月を選んでいてもこのコンポーネントから取得できるのは "日" です。
 */
export default {
  mixins: [ErrorReport, QueryFilterRestoreMixin],
  data() {
    return {
      termTypes: [
        { id: 'w', label: '週' },
        { id: 'm', label: '月' },
        { id: 'd', label: '日' },
      ],
      weekTypes: [
        { id: 'COUNT_FROM', label: 'から' },
        { id: 'COUNT_TO', label: 'まで' },
      ],
      // Snackbarの表示制御用のデータ
      snackbar: {
        show: false,
        duration: 10000,
        message: '',
      },
    }
  },
  components: { GlSelectMonth, GlInputDate },
  // WARN: 同じpropsのセットをFilterTermWithLimit.vueでも使用しています。
  //       このコンポーネントにpropsを追加する場合は、FilterTermWithLimit.vueも
  //       同様にpropsを追加してください。
  props: {
    // 選択している選択種別（日 / 週 / 月）
    termType: {
      type: String,
      default: 'w',
    },
    dateFrom: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    dateTo: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    defaultWeekCount: {
      type: Number,
      default: 13,
    },
    // 選択している選択種別（から / まで）
    weekType: {
      type: String,
      default: 'COUNT_FROM',
    },
  },
  watch: {
    /**
     * 最大日付の変更時、現在の日付値が上限を超えていないか確認し、
     * 超えている場合は新しい上限に自動調整します。
     */
    maxDate() {
      this.adjustRangeIfNeeded()
    },
    minDate() {
      this.adjustRangeIfNeeded()
    },
    /**
     * 選択種別の変更時、現在設定されている開始・終了日から、変更先の種別で適切となる
     * 開始・終了日に変換します。
     *
     * 例）"日" ⇒ "月" に変更した場合、"開始日" は月初の日付になり、"終了日" は月末の日付になります。
     *     2019/01/14～2019/05/03 -> 2019/01(2019/01/01)～2019/05(2019/05/31)
     */
    termType(value) {
      this.$emit('update:termType', value)
      let dateFrom = this.dateFrom
      let dateTo = this.dateTo
      // dateFrom, dateToはpropでもらい、親コンポーネントと.syncするので、
      // `update:dateFrom`, `update:dateTo` をemitするだけです。

      switch (value) {
        case 'w':
          // 週の場合終了日は表示されませんが、内部的には開始日からx週間後の日付を保持します。
          dateTo = getEndDateByWeekCount(dateFrom, this.weekCount)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

          this.$emit('update:dateTo', dateTo)
          break
        case 'm':
          dateFrom = moment(dateFrom)
            .startOf('month')
            .format('YYYYMMDD')
          dateTo = moment(dateTo)
            .endOf('month')
            .format('YYYYMMDD')
          dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
          this.$emit('update:dateFrom', dateFrom)
          this.$emit('update:dateTo', dateTo)
          break
        default:
          break
      }
    },
  },
  methods: {
    adjustRangeIfNeeded() {
      // QueryFilterRestoreMixinのcomputedを参照
      if (this._queryFilterRestoring) return

      const adjustedFrom = dateLimit(this.dateFrom, this.minDate, this.maxDate)
      const adjustedTo = dateLimit(this.dateTo, this.minDate, this.maxDate)

      if (adjustedFrom === this.dateFrom && adjustedTo === this.dateTo) {
        return
      }

      this.$emit('update:dateFrom', adjustedFrom)
      this.$emit('update:dateTo', adjustedTo)
      this.snackbar.message =
        '指定期間が選択可能な期間外だったため、自動調整しました。'
      this.snackbar.show = true
    },
    // QueryFilterRestoreMixinのmethodsを上書き
    // 分析条件の復元が完了したときの処理
    onQueryFilterRestoreCompleted() {
      this.adjustRangeIfNeeded()
    },
  },
  computed: {
    minDate() {
      return this.$store.getters.calendarEffectiveMinDate
    },
    maxDate() {
      return this.$store.getters.calendarEffectiveMaxDate
    },
    /**
     *ラジオボタンで押された期間のtermTypeを変更します
     */
    _termType: {
      get() {
        if (!this.termTypes.some(m => m.id === this.termType)) {
          this.$emit('update:termType', this.termTypes[0].id || 'd')
        }
        return this.termType
      },
      set(newVal) {
        this.$emit('update:termType', newVal)
      },
    },
    isDateMode() {
      return this.termType === 'd'
    },
    isWeekMode() {
      return this.termType === 'w'
    },
    isMonthMode() {
      return this.termType === 'm'
    },
    maxWeek() {
      if (this.weekType === 'COUNT_FROM') {
        // から
        return moment(this.maxDate).diff(this.dateFrom, 'weeks') + 1
      } else {
        // まで
        return moment(this.dateTo).diff(this.minDate, 'weeks') + 1
      }
    },
    weekCountFrom() {
      return this.weekType === 'COUNT_FROM'
    },
    weekCountTo() {
      return this.weekType === 'COUNT_TO'
    },
    /**
     * termTypeが週で開始日が変更された場合ここで変更保存をしています。
     * x週間の間隔を保つため終了日も一緒に変更保存。
     */
    _WeekFrom: {
      get() {
        return this.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        let dateTo = weekMove(this.dateTo, this.dateFrom, newVal)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
        this.$emit('update:dateTo', dateTo)
      },
    },
    _WeekTo: {
      get() {
        return this.dateTo
      },
      set(newVal) {
        let dateFrom = weekMove(this.dateFrom, this.dateTo, newVal)
        dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', dateFrom)
        this.$emit('update:dateTo', newVal)
      },
    },
    /**
     * termTypeが週で週数が変更された場合ここで変更保存をしています
     * 内部的に日付で期間を管理している為、週数と終了日をemitします
     * weekType（から、まで）によって処理を変えている
     */
    weekCount: {
      get() {
        return moment(this.dateTo).diff(moment(this.dateFrom), 'weeks') + 1
      },
      set(newVal) {
        if (this.weekCountFrom) {
          let dateTo = getEndDateByWeekCount(this.dateFrom, newVal)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
          this.$emit('update:dateTo', dateTo)
        } else if (this.weekCountTo) {
          let dateTo = this.dateTo
          newVal = getStartDateByWeekCount(dateTo, newVal)
          newVal = dateLimit(newVal, this.minDate, this.maxDate)
          this.$emit('update:dateFrom', newVal)
        }
      },
    },
    // このコンポーネントの入出力となる開始・終了日のフォーマットと、
    // inputに設定する値の日付フォーマットが異なるので変換用computedプロパティを用意
    /**
     * termTypeが日
     */
    _dateFrom: {
      get() {
        return this.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
      },
    },
    /**
     * termTypeが日
     */
    _dateTo: {
      get() {
        return this.dateTo
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateTo', newVal)
      },
    },
    /**
     * termTypeが月
     */
    monthFrom: {
      get() {
        return moment(this.dateFrom).format('YYYYMM')
      },
      set(newVal) {
        newVal = toBasicDateFormat(newVal)
        newVal = moment(newVal)
          .startOf('month')
          .format('YYYYMMDD')
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
      },
    },
    /**
     * termTypeが月
     */
    monthTo: {
      get() {
        return moment(this.dateTo).format('YYYYMM')
      },
      set(newVal) {
        newVal = toBasicDateFormat(newVal)
        newVal = moment(newVal)
          .endOf('month')
          .format('YYYYMMDD')
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateTo', newVal)
      },
    },
    /**
     * ～から、～までの選択肢が変更された際weekTypeを変更します
     */
    _weekType: {
      get() {
        return this.weekType
      },
      set(newVal) {
        this.$emit('update:weekType', newVal)
      },
    },
    errors() {
      if (this.dateFrom && this.dateTo) {
        if (this.dateFrom > this.dateTo) {
          return ['終了期間は開始期間以降を選択してください']
        } else {
          return []
        }
      } else {
        return ['期間を選択してください']
      }
    },
  },
  /**
   * APIから選択出来る期限のカレンダーを取得します
   */
  async mounted() {
    // 検索期間の初期値指定、任意の初期値がある場合はelseへ
    // 検索期限は外部APIから取得する必要がある為、ここに記述
    let dateFrom = this.dateFrom
    let dateTo = this.dateTo
    if (!dateFrom || !dateTo) {
      dateFrom = moment(this.maxDate)
        .subtract(this.defaultWeekCount, 'week')
        .add(1, 'days')
        .format('YYYYMMDD')
      dateTo = toBasicDateFormat(this.maxDate)
      // 検索期間が検索期限を超えていた場合の処理
      dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
      dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
      this.$emit('update:dateFrom', dateFrom)
      this.$emit('update:dateTo', dateTo)
    }
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.term-input {
  display: flex;
}

.week-count {
  width: 70px;
}

.divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
  width: 70px;
}
// 期間選択エリアの切替アニメーション
.v-enter-active,
.v-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
.v-enter {
  transform: translateX(-50%);
}
.v-leave-to {
  transform: translateX(50%);
}
</style>
